import React, { FunctionComponent, ReactNode, useContext, useMemo } from 'react'

import { ThemeProvider as StyledComponentsThemeProvider, useTheme } from 'styled-components/native'

import {
  getProductTypeMetadata,
  getProductTypeSupportedFontFamily,
  Theme,
  ThemeMetadata,
} from '@lyrahealth-inc/shared-app-logic'

import { ThemeType } from './ThemeProvider'
import { ProductTypeContext } from '../productType/productTypeContext'

export interface ThemeScopeProps {
  children: ReactNode
  /**
   * Force dark mode within this scope for Aurora.
   * Only applies when `isAuroraEnabled` is true.
   */
  forceDarkMode?: boolean
  /**
   * Force light mode within this scope. Takes precedence over the parent theme.
   */
  forceLightMode?: boolean
}

/**
 * A wrapper component that creates a scoped theme context for its children.
 * Use this to force dark or light mode for a specific UI area (like Messenger)
 * while the rest of the app remains in the user's preferred theme.
 *
 * When `forceDarkMode` is true, it automatically checks if Aurora is enabled
 * and only applies dark mode + Aurora theming for Aurora product type.
 *
 * @example
 * ```tsx
 * // Only the messenger chat area is dark (when Aurora is enabled)
 * const MessageScreen = () => {
 *   return (
 *     <Container>
 *       <AppHeader title="Messages" />
 *       <ThemeScope forceDarkMode>
 *         <MessengerChatArea />
 *       </ThemeScope>
 *     </Container>
 *   )
 * }
 * ```
 */
export const ThemeScope: FunctionComponent<ThemeScopeProps> = ({
  children,
  forceDarkMode = false,
  forceLightMode = false,
}) => {
  const parentTheme = useTheme() as ThemeType
  const { productType, isAuroraEnabled } = useContext(ProductTypeContext)

  const scopedTheme = useMemo(() => {
    // Only apply forced dark mode when Aurora is enabled
    const shouldForceDarkMode = forceDarkMode && isAuroraEnabled
    let isDarkMode = parentTheme.isDarkMode
    if (shouldForceDarkMode) isDarkMode = true
    if (forceLightMode) isDarkMode = false

    // Aurora theme is applied when forcing dark mode for Aurora
    const isAuroraTheme = shouldForceDarkMode ? true : parentTheme.isAuroraTheme
    const colorTheme = isDarkMode ? Theme.DARK : Theme.LIGHT

    const { colorTokens, assets, componentStyles } = getProductTypeMetadata(
      colorTheme,
      productType,
      isAuroraTheme,
    ) as ThemeMetadata
    const fontFamilyMetadata = getProductTypeSupportedFontFamily(productType, isAuroraTheme)

    return {
      ...parentTheme,
      colors: colorTokens,
      assets,
      componentStyles,
      isDarkMode,
      isAuroraTheme,
      fontFamilyMetadata,
    }
  }, [parentTheme, forceDarkMode, forceLightMode, isAuroraEnabled, productType])

  return <StyledComponentsThemeProvider theme={scopedTheme}>{children}</StyledComponentsThemeProvider>
}
